import React, { useState, useEffect } from 'react';
import { 
    PlusIcon, 
    PencilIcon, 
    TrashIcon, 
    MagnifyingGlassIcon,
    ExclamationTriangleIcon,
    InformationCircleIcon,
    CheckCircleIcon,
    ClockIcon,
    CalendarIcon,
    BuildingOfficeIcon,
    UserGroupIcon
} from '@heroicons/react/24/outline';
import { apiUrl } from '../../utils/api';

interface Building {
    _id: string;
    nombre: string;
    direccion: string;
    ciudad: string;
}

interface Property {
    _id: string;
    numero: string;
    tipo: string;
    descripcion: string;
    edificio: Building;
}

interface Announcement {
    _id: string;
    titulo: string;
    contenido: string;
    nivelImportancia: 'baja' | 'media' | 'alta';
    fechaInicio: string;
    fechaFin: string;
    horaInicio: string;
    horaFin: string;
    edificios: Building[];
    propiedades: Property[];
    tiposUsuario: string[];
    activo: boolean;
    creadoPor: {
        _id: string;
        nombre: string;
        apellido: string;
        email: string;
    };
    createdAt: string;
    updatedAt: string;
}

interface AnnouncementFormData {
    titulo: string;
    contenido: string;
    nivelImportancia: 'baja' | 'media' | 'alta';
    fechaInicio: string;
    fechaFin: string;
    horaInicio: string;
    horaFin: string;
    edificios: string[];
    propiedades: string[];
    tiposUsuario: string[];
    archivosAdjuntos: any[];
}

const AnnouncementManagement: React.FC = () => {
    const [announcements, setAnnouncements] = useState<Announcement[]>([]);
    const [buildings, setBuildings] = useState<Building[]>([]);
    const [properties, setProperties] = useState<Property[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterImportance, setFilterImportance] = useState<string>('');
    const [filterStatus, setFilterStatus] = useState<string>('');
    const [showModal, setShowModal] = useState(false);
    const [editingAnnouncement, setEditingAnnouncement] = useState<Announcement | null>(null);
    const [formData, setFormData] = useState<AnnouncementFormData>({
        titulo: '',
        contenido: '',
        nivelImportancia: 'media',
        fechaInicio: '',
        fechaFin: '',
        horaInicio: '00:00',
        horaFin: '23:59',
        edificios: [],
        propiedades: [],
        tiposUsuario: ['socio', 'propietario', 'inversor'],
        archivosAdjuntos: []
    });

    useEffect(() => {
        fetchAnnouncements();
        fetchBuildings();
        fetchProperties();
    }, []);

    const fetchAnnouncements = async () => {
        try {
            const token = localStorage.getItem('token');
            const params = new URLSearchParams();
            if (searchTerm) params.append('search', searchTerm);
            if (filterImportance) params.append('nivelImportancia', filterImportance);
            if (filterStatus) params.append('activo', filterStatus);
            
            const url = apiUrl(`/api/announcements?${params}`);
            console.log('Obteniendo anuncios desde:', url);
            
            const response = await fetch(url, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            console.log('Respuesta de anuncios:', response.status, response.statusText);

            if (response.ok) {
                const data = await response.json();
                console.log('Datos de anuncios recibidos:', data);
                setAnnouncements(data.data.announcements);
            } else {
                const error = await response.json();
                console.error('Error al obtener anuncios:', error);
            }
        } catch (error) {
            console.error('Error:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchBuildings = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(apiUrl('/api/buildings'), {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                setBuildings(data.data.buildings);
            } else {
                console.error('Error al obtener edificios');
            }
        } catch (error) {
            console.error('Error al obtener edificios:', error);
        }
    };

    const fetchProperties = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(apiUrl('/api/admin/properties'), {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                setProperties(data.data.properties);
            } else {
                console.error('Error al obtener propiedades');
            }
        } catch (error) {
            console.error('Error al obtener propiedades:', error);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        try {
            const token = localStorage.getItem('token');
            const url = editingAnnouncement ? apiUrl(`/api/announcements/${editingAnnouncement._id}`) : apiUrl('/api/announcements');
            const method = editingAnnouncement ? 'PUT' : 'POST';
            
            console.log('Enviando datos:', formData);
            console.log('URL:', url);
            console.log('Método:', method);
            
            const response = await fetch(url, {
                method,
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(formData)
            });

            console.log('Respuesta del servidor:', response.status, response.statusText);

            if (response.ok) {
                const result = await response.json();
                console.log('Anuncio creado/actualizado:', result);
                await fetchAnnouncements();
                setShowModal(false);
                setEditingAnnouncement(null);
                setFormData({
                    titulo: '',
                    contenido: '',
                    nivelImportancia: 'media',
                    fechaInicio: '',
                    fechaFin: '',
                    horaInicio: '00:00',
                    horaFin: '23:59',
                    edificios: [],
                    propiedades: [],
                    tiposUsuario: ['socio', 'propietario', 'inversor'],
                    archivosAdjuntos: []
                });
                alert('Anuncio guardado exitosamente');
            } else {
                const error = await response.json();
                console.error('Error del servidor:', error);
                alert(error.message || 'Error al guardar anuncio');
            }
        } catch (error) {
            console.error('Error:', error);
            alert('Error al guardar anuncio');
        }
    };

    const handleEdit = (announcement: Announcement) => {
        setEditingAnnouncement(announcement);
        setFormData({
            titulo: announcement.titulo,
            contenido: announcement.contenido,
            nivelImportancia: announcement.nivelImportancia,
            fechaInicio: announcement.fechaInicio.split('T')[0],
            fechaFin: announcement.fechaFin.split('T')[0],
            horaInicio: announcement.horaInicio || '00:00',
            horaFin: announcement.horaFin || '23:59',
            edificios: announcement.edificios.map(e => e._id),
            propiedades: announcement.propiedades.map(p => p._id),
            tiposUsuario: announcement.tiposUsuario,
            archivosAdjuntos: []
        });
        setShowModal(true);
    };

    const handleDelete = async (announcementId: string) => {
        if (!window.confirm('¿Estás seguro de que quieres eliminar este anuncio?')) {
            return;
        }

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(apiUrl(`/api/announcements/${announcementId}`), {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                await fetchAnnouncements();
            } else {
                const error = await response.json();
                alert(error.message || 'Error al eliminar anuncio');
            }
        } catch (error) {
            console.error('Error:', error);
            alert('Error al eliminar anuncio');
        }
    };

    const getImportanceIcon = (nivel: string) => {
        switch (nivel) {
            case 'alta':
                return <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />;
            case 'media':
                return <InformationCircleIcon className="h-5 w-5 text-yellow-500" />;
            case 'baja':
                return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
            default:
                return <InformationCircleIcon className="h-5 w-5 text-gray-500" />;
        }
    };

    const getImportanceLabel = (nivel: string) => {
        const labels: { [key: string]: string } = {
            'alta': 'Alta',
            'media': 'Media',
            'baja': 'Baja'
        };
        return labels[nivel] || nivel;
    };

    const getImportanceColor = (nivel: string) => {
        switch (nivel) {
            case 'alta':
                return 'bg-red-100 text-red-800';
            case 'media':
                return 'bg-yellow-100 text-yellow-800';
            case 'baja':
                return 'bg-green-100 text-green-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    const isActive = (fechaInicio: string, fechaFin: string) => {
        const now = new Date();
        const inicio = new Date(fechaInicio);
        const fin = new Date(fechaFin);
        return now >= inicio && now <= fin;
    };

    const filteredAnnouncements = announcements.filter(announcement => {
        const matchesSearch = !searchTerm || 
            announcement.titulo.toLowerCase().includes(searchTerm.toLowerCase()) ||
            announcement.contenido.toLowerCase().includes(searchTerm.toLowerCase());
        
        const matchesImportance = !filterImportance || announcement.nivelImportancia === filterImportance;
        const matchesStatus = !filterStatus || announcement.activo.toString() === filterStatus;
        
        return matchesSearch && matchesImportance && matchesStatus;
    });

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
                <div>
                    <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-2">Portal Inversionistas</h1>
                    <h2 className="text-2xl font-semibold text-gray-600">Gestión de Anuncios</h2>
                    <p className="text-gray-600 mt-2">Administra anuncios del tablón de noticias</p>
                </div>
                <button
                    onClick={() => {
                        setEditingAnnouncement(null);
                        setFormData({
                            titulo: '',
                            contenido: '',
                            nivelImportancia: 'media',
                            fechaInicio: '',
                            fechaFin: '',
                            horaInicio: '00:00',
                            horaFin: '23:59',
                            edificios: [],
                            propiedades: [],
                            tiposUsuario: ['socio', 'propietario', 'inversor'],
                            archivosAdjuntos: []
                        });
                        setShowModal(true);
                    }}
                    className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center justify-center space-x-2"
                >
                    <PlusIcon className="h-4 w-4" />
                    <span>Nuevo Anuncio</span>
                </button>
            </div>

            {/* Filtros y Búsqueda */}
            <div className="bg-white p-4 rounded-lg shadow-md border">
                <div className="flex flex-col sm:flex-row gap-4">
                    <div className="flex-1">
                        <div className="relative">
                            <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Buscar por título o contenido..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <select
                            value={filterImportance}
                            onChange={(e) => setFilterImportance(e.target.value)}
                            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                            <option value="">Todos los niveles</option>
                            <option value="alta">Alta importancia</option>
                            <option value="media">Media importancia</option>
                            <option value="baja">Baja importancia</option>
                        </select>
                        <select
                            value={filterStatus}
                            onChange={(e) => setFilterStatus(e.target.value)}
                            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                            <option value="">Todos los estados</option>
                            <option value="true">Activo</option>
                            <option value="false">Inactivo</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Lista de Anuncios */}
            <div className="space-y-4">
                {filteredAnnouncements.map((announcement) => (
                    <div key={announcement._id} className="bg-white rounded-lg shadow-md border p-6">
                        <div className="flex justify-between items-start mb-4">
                            <div className="flex items-center space-x-3">
                                {getImportanceIcon(announcement.nivelImportancia)}
                                <h3 className="text-lg font-semibold text-gray-900">
                                    {announcement.titulo}
                                </h3>
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getImportanceColor(announcement.nivelImportancia)}`}>
                                    {getImportanceLabel(announcement.nivelImportancia)}
                                </span>
                            </div>
                            <div className="flex space-x-2">
                                <button
                                    onClick={() => handleEdit(announcement)}
                                    className="text-blue-600 hover:text-blue-900"
                                >
                                    <PencilIcon className="h-4 w-4" />
                                </button>
                                <button
                                    onClick={() => handleDelete(announcement._id)}
                                    className="text-red-600 hover:text-red-900"
                                >
                                    <TrashIcon className="h-4 w-4" />
                                </button>
                            </div>
                        </div>

                        <p className="text-gray-700 mb-4">{announcement.contenido}</p>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600">
                            <div className="flex items-center space-x-2">
                                <CalendarIcon className="h-4 w-4" />
                                <span>
                                    {new Date(announcement.fechaInicio).toLocaleDateString()} {announcement.horaInicio} - {new Date(announcement.fechaFin).toLocaleDateString()} {announcement.horaFin}
                                </span>
                            </div>
                            
                            <div className="flex items-center space-x-2">
                                <UserGroupIcon className="h-4 w-4" />
                                <span>{announcement.tiposUsuario.join(', ')}</span>
                            </div>
                            
                            <div className="flex items-center space-x-2">
                                <BuildingOfficeIcon className="h-4 w-4" />
                                <span>
                                    {announcement.edificios.length > 0 
                                        ? `${announcement.edificios.length} edificio(s)`
                                        : 'Todos los edificios'
                                    }
                                </span>
                            </div>
                        </div>

                        <div className="mt-4 flex justify-between items-center">
                            <div className="flex items-center space-x-2">
                                <ClockIcon className="h-4 w-4 text-gray-400" />
                                <span className="text-sm text-gray-500">
                                    Creado por {announcement.creadoPor.nombre} {announcement.creadoPor.apellido}
                                </span>
                            </div>
                            <div className="flex items-center space-x-2">
                                {isActive(announcement.fechaInicio, announcement.fechaFin) ? (
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                        Activo
                                    </span>
                                ) : (
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                        Inactivo
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Modal de Anuncio */}
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
                        <h2 className="text-xl font-bold text-gray-900 mb-4">
                            {editingAnnouncement ? 'Editar Anuncio' : 'Nuevo Anuncio'}
                        </h2>
                        
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Título
                                    </label>
                                    <input
                                        type="text"
                                        required
                                        value={formData.titulo}
                                        onChange={(e) => setFormData({ ...formData, titulo: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Nivel de Importancia
                                    </label>
                                    <select
                                        required
                                        value={formData.nivelImportancia}
                                        onChange={(e) => setFormData({ ...formData, nivelImportancia: e.target.value as any })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    >
                                        <option value="baja">Baja</option>
                                        <option value="media">Media</option>
                                        <option value="alta">Alta</option>
                                    </select>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Contenido
                                </label>
                                <textarea
                                    required
                                    value={formData.contenido}
                                    onChange={(e) => setFormData({ ...formData, contenido: e.target.value })}
                                    rows={4}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Fecha de Inicio
                                    </label>
                                    <input
                                        type="date"
                                        required
                                        value={formData.fechaInicio}
                                        onChange={(e) => setFormData({ ...formData, fechaInicio: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Fecha de Fin
                                    </label>
                                    <input
                                        type="date"
                                        required
                                        value={formData.fechaFin}
                                        onChange={(e) => setFormData({ ...formData, fechaFin: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Hora de Inicio
                                    </label>
                                    <input
                                        type="time"
                                        value={formData.horaInicio}
                                        onChange={(e) => setFormData({ ...formData, horaInicio: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Hora de Fin
                                    </label>
                                    <input
                                        type="time"
                                        value={formData.horaFin}
                                        onChange={(e) => setFormData({ ...formData, horaFin: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Tipos de Usuario
                                </label>
                                <div className="flex space-x-4">
                                    {['socio', 'propietario', 'inversor'].map(tipo => (
                                        <label key={tipo} className="flex items-center">
                                            <input
                                                type="checkbox"
                                                checked={formData.tiposUsuario.includes(tipo)}
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        setFormData({
                                                            ...formData,
                                                            tiposUsuario: [...formData.tiposUsuario, tipo]
                                                        });
                                                    } else {
                                                        setFormData({
                                                            ...formData,
                                                            tiposUsuario: formData.tiposUsuario.filter(t => t !== tipo)
                                                        });
                                                    }
                                                }}
                                                className="mr-2"
                                            />
                                            <span className="capitalize">{tipo}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Edificios (opcional - si no se selecciona ninguno, será visible en todos)
                                </label>
                                <select
                                    multiple
                                    value={formData.edificios}
                                    onChange={(e) => {
                                        const selected = Array.from(e.target.selectedOptions, option => option.value);
                                        setFormData({ ...formData, edificios: selected });
                                    }}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                >
                                    {buildings.map(building => (
                                        <option key={building._id} value={building._id}>
                                            {building.nombre} - {building.ciudad}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Propiedades Específicas (opcional)
                                </label>
                                <select
                                    multiple
                                    value={formData.propiedades}
                                    onChange={(e) => {
                                        const selected = Array.from(e.target.selectedOptions, option => option.value);
                                        setFormData({ ...formData, propiedades: selected });
                                    }}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                >
                                    {properties.map(property => (
                                        <option key={property._id} value={property._id}>
                                            {property.numero} - {property.edificio.nombre}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="flex justify-end space-x-3 pt-4">
                                <button
                                    type="button"
                                    onClick={() => setShowModal(false)}
                                    className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                                >
                                    {editingAnnouncement ? 'Actualizar' : 'Crear'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AnnouncementManagement;
